import cn from 'classnames'
import React, {
    forwardRef,
    ButtonHTMLAttributes,
    JSXElementConstructor,
    useRef,
    ReactNode
} from 'react'
import s from './Button.module.scss'
import { LoadingDots } from '@corratech/pylot-ui'

type ComponentType = string | JSXElementConstructor<any>

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    href?: string
    className?: string
    variant?:
        | 'flat'
        | 'slim'
        | 'primary'
        | 'secondary'
        | 'link'
        | 'quickAdd'
        | string
    active?: boolean
    type?: 'submit' | 'reset' | 'button'
    Component?: ComponentType
    width?: string | number
    loading?: boolean
    disabled?: boolean
    children?: ReactNode
}

const Button = forwardRef<
    HTMLButtonElement | string | JSXElementConstructor<ComponentType>,
    ButtonProps
>((props) => {
    const {
        className,
        variant = 'flat',
        children,
        active,
        width,
        loading = false,
        disabled = false,
        style = {},
        Component = 'button',
        ...rest
    } = props
    const ref = useRef<typeof Component>(null)
    const rootClassName = cn(
        s.root,
        variant === 'primary' && `${[s.primary]} btn-primary`,
        variant === 'secondary' && `${[s.secondary]} btn-secondary`,
        variant === 'slim' && `${[s.slim]} btn-slim`,
        variant === 'link' && `${[s.link]} btn-link`,
        variant === 'quickAdd' && `${[s.quickAdd]} btn-quickAdd`,

        {
            [s.loading]: loading,
            [s.disabled]: disabled
        },
        className
    )

    return (
        <Component
            aria-pressed={active}
            data-variant={variant}
            aria-label={variant}
            ref={ref}
            className={rootClassName}
            disabled={disabled}
            style={{
                width,
                ...style
            }}
            {...rest}
        >
            {variant !== 'link' ? (
                <span>
                    {children}
                    {loading && (
                        <i className="pl-2 m-0 flex">
                            <LoadingDots />
                        </i>
                    )}
                </span>
            ) : (
                <>
                    {children}
                    {loading && (
                        <i className="pl-2 m-0 flex">
                            <LoadingDots />
                        </i>
                    )}
                </>
            )}
        </Component>
    )
})

export default Button
